<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useOrgStore } from '@/stores/org'
import { useLayersStore } from '@/stores/layers'
import { useMapsStore } from '@/stores/maps'
import { useRoute, useRouter } from 'vue-router'

import { Check } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MAP_COLORS, MAP_ICON_NAMES, MAP_ICONS } from '@/components/maps/mapIcons'
import BasemapPicker from '@/components/maps/BasemapPicker.vue'
import { DEFAULT_BASEMAP_KEY } from '@/components/maps/basemaps'

const orgStore = useOrgStore()

const layersStore = useLayersStore()

const mapsStore = useMapsStore()
const route = useRoute()
const router = useRouter()

const orgId = computed(() => orgStore.selectedOrg as string)
const mapId = computed(() => route.params.map_id as string | undefined)
const editMode = computed(() => !!mapId.value)

const name = ref('')
const color = ref(MAP_COLORS[0])
const icon = ref('Map')
const basemap = ref(DEFAULT_BASEMAP_KEY)
const shared = ref(false)

const init = async () => {
  await layersStore.fetchOrgLayers({ orgId: orgId.value })
  await mapsStore.fetchMaps({ orgId: orgId.value })

  if (editMode.value) {
    const map = mapsStore.getMapById(orgId.value, mapId.value)
    name.value = map?.name || ''
    color.value = map?.color || MAP_COLORS[0]
    icon.value = map?.icon || 'Map'
    basemap.value = map?.basemap || DEFAULT_BASEMAP_KEY
    shared.value = !!map?.shared
  } else {
    name.value = ''
    color.value = MAP_COLORS[0]
    icon.value = 'Map'
    basemap.value = DEFAULT_BASEMAP_KEY
    shared.value = false
  }
}

watch(mapId, init, { immediate: true })

const goBack = () => {
  router.push({ name: 'OrgLayers', params: { org_id: orgId.value } })
}

const submit = () => {
  const trimmed = name.value.trim()
  if (!trimmed) return

  if (editMode.value && mapId.value) {
    mapsStore.renameMap({ orgId: orgId.value, mapId: mapId.value, name: trimmed })
    mapsStore.setMapStyle({ orgId: orgId.value, mapId: mapId.value, color: color.value, icon: icon.value, basemap: basemap.value, shared: shared.value })
  } else {
    mapsStore.createMap({ orgId: orgId.value, name: trimmed, color: color.value, icon: icon.value, basemap: basemap.value, shared: shared.value })
  }
  goBack()
}
</script>

<template>
  <div class="px-3 py-2" data-testid="map-edit-page">
    <div class="mb-4">
      <h2 class="text-lg font-semibold">{{ editMode ? 'Settings' : 'New map' }}</h2>
    </div>

    <div class="space-y-4 max-w-[380px]">
      <div>
        <Label class="mb-1">Name</Label>
        <Input v-model.trim="name" placeholder="Map name" @keydown.enter="submit" autofocus />
      </div>

      <div>
        <Label class="mb-1.5 block">Color</Label>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="c in MAP_COLORS"
            :key="c"
            type="button"
            class="w-6 h-6 rounded-full flex items-center justify-center ring-offset-2 ring-offset-background transition"
            :class="color === c ? 'ring-2 ring-primary' : ''"
            :style="{ backgroundColor: c }"
            @click="color = c"
          >
            <Check v-if="color === c" class="w-3.5 h-3.5 text-white" />
          </button>
        </div>
      </div>

      <div>
        <Label class="mb-1.5 block">Icon</Label>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="n in MAP_ICON_NAMES"
            :key="n"
            type="button"
            class="w-8 h-8 rounded-md border flex items-center justify-center transition"
            :class="icon === n ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-muted'"
            @click="icon = n"
          >
            <component :is="MAP_ICONS[n]" class="w-4 h-4" />
          </button>
        </div>
      </div>

      <div>
        <Label class="mb-1.5 block">Basemap</Label>
        <BasemapPicker v-model="basemap" :org-id="orgId" />
      </div>

      <!-- <div class="flex items-start justify-between gap-3 rounded-md border border-border p-3">
        <div class="min-w-0">
          <Label class="block">Public</Label>
          <p class="text-xs text-muted-foreground mt-0.5">
            When on, this map is visible outside your organization — anyone with the link can view it.
          </p>
        </div>
        <Switch :checked="shared" @update:checked="shared = $event" class="flex-shrink-0 mt-0.5" />
      </div> -->

      <div class="flex items-center justify-end gap-2 pt-2">
        <Button variant="outline" @click="goBack">Cancel</Button>
        <Button :disabled="!name.trim()" @click="submit">{{ editMode ? 'Save' : 'Create map' }}</Button>
      </div>
    </div>
  </div>
</template>
